import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Controller, useForm, FormProvider } from 'react-hook-form';
import { UNAUTHORIZED, FORBIDDEN, UNPROCESSABLE_ENTITY } from 'http-status-codes';
import { yupResolver } from '@hookform/resolvers/yup';
import { object, string } from 'yup';
import { useHistory, useParams } from 'react-router-dom';
import isEmpty from 'lodash/isEmpty';
import get from 'lodash/get';
import TextField from '@material-ui/core/TextField';
import Button from '@material-ui/core/Button';
import { Row } from 'components/UI/Row';
import { BottomActionBar } from 'components/UI/BottomActionBar';
import { Divider } from 'components/UI/Divider';
import Alert from '@material-ui/lab/Alert';
import Snackbar from '@material-ui/core/Snackbar';
import { aclPrefix, aclPoliciesPrefix } from 'routes/routes';
import { ButtonLoader } from 'components/Loaders/ButtonLoader';

import { axiosInstance } from '../../axiosInstance';
import { Actions } from './Actions';
import { Groups } from './Groups';
import { Resources } from './Resources';

export type PolicyFormData = {
  actions: string[];
  name: string;
  resources: any;
  user_groups: string[];
};

type LinkedResource = {
  link_id: string;
  link_type: string;
};

const PolicyFormSchema = object().shape({
  name: string().required('acl.policy.errors.name required')
});

const PolicyForm = () => {
  const { t } = useTranslation();
  const history = useHistory();
  const { policyId } = useParams<{ policyId: string }>();
  // TODO: hook
  const [isLoading, setIsLoading] = useState<boolean>(!isEmpty(policyId));
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const [savingError, setSavingError] = useState<string>('');
  // END TODO
  const [checkedResources, setCheckedResources] = useState<LinkedResource[]>([]);
  const methods = useForm<PolicyFormData>({
    mode: 'onBlur',
    resolver: yupResolver(PolicyFormSchema)
  });
  const { handleSubmit, errors, control, reset, watch } = methods;
  const watchResources = watch(`resources`);

  // TODO: hook management or just move out from this
  useEffect(() => {
    const loadPolicy = async () => {
      setIsLoading(true);
      let response: any;
      try {
        response = await axiosInstance.get(`/acls/policies/${policyId}`);
      } catch (e) {
        console.log(e);
      } finally {
        const {
          data: { actions, name, user_groups, resources }
        } = response;
        reset({ name, actions, user_groups, resources });
        setCheckedResources(resources);
        setIsLoading(false);
      }
    };
    if (policyId) {
      loadPolicy();
    }
  }, []);

  const onSubmit = async (formData: PolicyFormData) => {
    // manage errors
    try {
      if (policyId) {
        setIsSaving(true);
        await axiosInstance.put(`/acls/policies/${policyId}`, formData);
      } else {
        const {
          data: { id }
        } = await axiosInstance.post('/acls/policies', formData);
        history.replace(`${aclPrefix}${aclPoliciesPrefix}/${id}`);
      }
    } catch (e) {
      if (!e.response) {
        setSavingError(t('errors.something went wrong'));
        return;
      }
      const { status } = e.response;
      if (status === UNAUTHORIZED || status === FORBIDDEN) {
        setSavingError(t('errors.unauthorized'));
        return;
      }

      if (status === UNPROCESSABLE_ENTITY) {
        setSavingError(t('errors.user not registered on 4boxes'));
        return;
      }

      setSavingError(t('errors.something went wrong'));
    } finally {
      setIsSaving(false);
    }
  };

  useEffect(() => {
    setCheckedResources(watchResources || []);
  }, []);

  if (isLoading) {
    return <>LOADING</>;
  }

  return (
    <FormProvider {...methods}>
      <form onSubmit={handleSubmit(onSubmit)} noValidate>
        <Controller
          as={
            <TextField
              fullWidth
              autoComplete="new-password"
              helperText={t(get(errors, 'name.message'))}
              error={!isEmpty(get(errors, `name`))}
              label={t('acl.policy name')}
              style={{ marginBottom: '30px' }}
            />
          }
          control={control}
          defaultValue=""
          name="name"
        />
        <Actions />
        {policyId && !isLoading && (
          <>
            <Groups />
            <div style={{ margin: '50px 0 25px' }}>
              <Divider color="lightgrey" />
            </div>
            <h3>{t('acl.resources')}</h3>
            <Resources
              linkType="product"
              searchLabel="product"
              title={t('acl.products')}
              checkedValues={checkedResources}
              setCheckedValues={setCheckedResources}
            />
            <Resources
              linkType="serial_number"
              searchLabel="serial_number"
              title={t('acl.serial numbers')}
              checkedValues={checkedResources}
              setCheckedValues={setCheckedResources}
            />
          </>
        )}

        <div style={{ height: '90px' }} />
        <BottomActionBar>
          <Row alignItems="center" justifyContent="flex-end" margin="0 40px 0 0">
            <Button color="primary" disabled={isLoading || isSaving} variant="contained" type="submit">
              {t('label.save')}
              <ButtonLoader show={isSaving} />
            </Button>
          </Row>
        </BottomActionBar>
      </form>
      <Snackbar
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        key="bottom,center"
        open={!isEmpty(savingError)}
        autoHideDuration={5000}
        onClose={() => {
          setSavingError('');
        }}
        style={{ bottom: '10px' }}
      >
        <Alert severity="error" variant="filled" onClose={() => setSavingError('')}>
          {savingError}
        </Alert>
      </Snackbar>
    </FormProvider>
  );
};

export { PolicyForm };
